import React, { useState } from "react";
import { IconSvg, IconSvgSizes } from "@att-bit/duc.components.icon-svg";
import { AnalyticsEvent } from "@att-bit/ui-frameworks.components.analytics-event";
import { docComplete } from "@att-bit/duc.utils.types";
import { useUniqueId } from '@att-bit/duc.utils.custom-useid';
import {
  BackgroundVideo,
  BackgroundVideoInterface,
} from "@att-bit/duc.fragments.background-video";
import { Modal, ModalSize } from "@att-bit/duc.components.modal";

export enum LinksVariant {
  textUnderline = "link-text",
  /** A text link with medium font. */
  textMedium = "link-text2",
  /** A text link without any distinguishing features. Use this very sparingly. As in almost never. */
  textPlain = "link-text3",
  /** A link with Icon prepended before text. */
  withPrependIcon = "link-icon-left",
  /** A link with Icon appended after text. */
  withAppendIcon = "link-icon-right",
  /** A link with Icon appended */
  withIcon = "link-icon",
  /** A link that is blue on light backgrounds or white on dark backgrounds, with a rightward caret. */
  PrimaryCTA = "primary-cta",
  /** A link that is blue on light backgrounds or white on dark backgrounds, with a rightward caret. */
  basePrimaryCta = "primary-cta type-base",
  /** type base link that is blue on light backgrounds or white on dark backgrounds, with a rightward caret. */
  smallPrimaryCta = "primary-cta type-sm",
  /* text plain with bold*/
  soloTextPlain = "link-text3 solo",
  /** type regular small link that is black and turn blue on hover. */
  smallSoloTextUnderline = "link-text solo type-sm font-regular",
  /** type none*/
  none = "",
  /** A link no default classes */
 
}

// TO check next js dependency
export type LinksProps = {
  /**  children (text/jsx) to be rendered in component  */
  children?: any;
  /** variant is of Kind enum textUnderline | textMedium | textPlain | withIcon */
  variant?: LinksVariant;
  /**  Whether this CTA is being rendered on a dark background. Defaults to `false  */
  onDark?: boolean;
  /** Sets the class to display App download image  */
  appsDownload?: string;
  /** Sets analytics linkName */
  linkName?: string;
  /**used to supress link analytics */
  suppressAnalytics?: boolean;
  /** To implement as nextLink */
  nextLink?: boolean;
  /** To set an cta-overlay */
  overlay?: boolean;
  /** To load Apple svg badge */
  svgApple?: boolean;
  /** To load Google svg badge */
  svgGoogle?: boolean;
  /**href attribute */
  href?: any;
  /** additional analytics data*/
  additionalAnalyticsData?: any;
  /** if modal is true */
  isModal?: boolean;
  /** modal children */
  videoContent?: any;
  modalChildren?: any;
  /** Accessibility text */
  accessibilityText?: string;
  appleStoreAltText?: any;
  googleStoreAltText?: any;
  /** if new tab is true */
  isNewTab?: boolean;
  /** if we want to pass a custom margin class to the component */
  margin?: string[];
  namedLinkedAnchor?: string;
  /** to forwardref from parent component */
  anchorRef?: React.ForwardedRef<HTMLAnchorElement>
};

const LinksIconSvg = (props) => {
  return (
    <>
      <IconSvg {...props} />
    </>
  );
};
Links.IconSvg = LinksIconSvg;

export function Links({
  children,
  variant = LinksVariant.textPlain,
  onDark = false,
  appsDownload,
  linkName,
  onClick,
  suppressAnalytics = false,
  nextLink = false,
  overlay,
  svgApple = false,
  svgGoogle = false,
  href,
  additionalAnalyticsData,
  isModal,
  videoContent,
  modalChildren,
  appleStoreAltText = "Apple Playstore SVG",
  googleStoreAltText = "Google Playstore SVG",
  isNewTab,
  margin,
  namedLinkedAnchor,
  anchorRef,
  accessibilityText,
  ...props
}: LinksProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  // if (nextLink) {
  //   const Link = require('next/link');
  // }
  let videoData;
  // HOOKS ##############################################################

  // METHODS #############################################################
  const handleKeyEvent = (e: any) => {
    const { key } = e;
    // istanbul ignore next
    if (key === "Enter" || key === " ") {
      if (!suppressAnalytics) {
        sendAnalyticsEvent(e);
      }
      if (isModal) {
        getModalData(e);
      }
      if (onClick) {
        onClick(e);
      }
    }
  };

  const sendAnalyticsEvent = (e: any) => {
    const localadtnlAnalyticsData = additionalAnalyticsData && {...additionalAnalyticsData};
    const lnkname =
    localadtnlAnalyticsData?.linkName != undefined
        ? localadtnlAnalyticsData.linkName
        : linkName
        ? linkName
        : e.target?.innerText;
    const destUrl =
    localadtnlAnalyticsData?.linkDestinationUrl != undefined
        ? localadtnlAnalyticsData.linkDestinationUrl
        : e.target?.href;
    const lnkPos =
    localadtnlAnalyticsData?.linkPosition != undefined
        ? localadtnlAnalyticsData.linkPosition
        : "Body";
    if (localadtnlAnalyticsData) {
      delete localadtnlAnalyticsData.linkName;
      delete localadtnlAnalyticsData.linkDestinationUrl;
      delete localadtnlAnalyticsData.linkPosition;
    }
    const data = {
      linkDestinationUrl: destUrl,
      linkName: lnkname,
      linkPosition: lnkPos,
      ...localadtnlAnalyticsData,
    };
    const lnkClickObj = {
      eventCode: "Link_Click",
      eventAction: "linkClick",
      additionalData: data,
    };
    docComplete(() => {
      AnalyticsEvent(lnkClickObj);
    });
  };

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (videoContent && videoContent.videoId) {
      videoData = BackgroundVideoInterface.initWithSanity("modalBgContent", {
        modal: false,
        ...videoContent,
      });
      setShowVideo(videoData);
    }
    // istanbul ignore next
    if (!suppressAnalytics) {
      sendAnalyticsEvent(e);
    }

    if (isModal) {
      getModalData(e);
    }
    // istanbul ignore next
    if (onClick) {
      onClick(e);
    }
  };

  const renderNextLink = () => {
    const Link = require("next/link");
    return (
      <Link href={nextHref}>
        <a
          className={[
            `${propsClassNames} ${marginClasses}`,
            variant === LinksVariant.withIcon
              ? onDark
                ? `color-white font-bold link-icon hover`
                : `color-cobalt font-bold link-icon hover}`
              : variant,
          ].join(" ")}
          onKeyDown={handleKeyEvent}
          onClick={handleOnClick}
          target={isNewTab && "_blank"}
          aria-label={accessibilityText}
          ref={anchorRef}
          {...props}
        >
          {React.Children.map(children, (child, idx) => {
            if (React.isValidElement(child) && child.type === Link.IconSvg) {
              return React.cloneElement(child as React.ReactElement<any>, {
                idx,
              });
            }
            if (hasIcon) {
              return <span>{child}</span>;
            } else {
              return child;
            }
          })}
        </a>
      </Link>
    );
  };
  // RENDER ##############################################################
  let hasIcon = false;
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === Links.IconSvg) {
      hasIcon = true;
    }
  });

  let marginClasses=" ";
  const propsClassNames = props.className || "";
  const linkId = useUniqueId({variant,appsDownload,linkName,href,modalChildren,namedLinkedAnchor,margin,svgApple,svgGoogle,anchorRef,videoContent,appleStoreAltText,...props,additionalAnalyticsData},'links',children)
  if (margin) {
    marginClasses = margin?.join(" ");
  }

  delete props.className;
  const nextHref = href || undefined;

  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState(null);
  const [showVideo, setShowVideo] = useState(null);
  const handleCallbackForVideo = (isVideoDone) => {
    if (!isVideoDone) setShowVideo(null);
  };
  const closeModal = () => {
    setShowModal(false);
  };
  const getModalData = (e) => {
    e.preventDefault();
    let modalContent;
    //const modalBackgroundContent = modalChildren?.modalBackgroundContent ? BackgroundVideoInterface.initWithSanity('modalBgContent', modalChildren?.modalBackgroundContent) : undefined;
    if (modalChildren?.modalContent) {
      modalContent = () => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: modalChildren?.modalContent?.text,
            }}
          />
        );
      };
    }
    //modalBackgroundContent && setModalBody(modalBackgroundContent?.toJsx());
    modalContent && setModalBody(modalContent());
    setShowModal(true);
  };

  return (
    <>
      {nextLink && nextHref ? (
        renderNextLink()
      ) : appsDownload ? (
        <>
          <a
            className={[
              `${propsClassNames} ${marginClasses}`,
              overlay ? "cta-overlay" : "",
              appsDownload === "apple-lg" ||
              appsDownload === "google-lg" ||
              appsDownload === "apple-sm" ||
              appsDownload === "google-sm"
                ? "inline-flex"
                : "",
              variant === LinksVariant.withIcon
                ? onDark
                  ? `color-white font-bold link-icon hover`
                  : `color-cobalt font-bold link-icon hover`
                : variant,
            ].join(" ")}
            onKeyDown={handleKeyEvent}
            onClick={handleOnClick}
            aria-label={accessibilityText}
            target={isNewTab && "_blank"}
            {...{ href: href }}
            {...{ tabIndex: href ? undefined : 0 }}
            {...{
              role: !props.role ? (!href ? "button" : undefined) : undefined,
            }}
            ref={anchorRef}
            {...props}
          >
            {appsDownload === "apple-lg" || appsDownload === "apple-sm" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 144 48"
                className={
                  appsDownload == "apple-lg"
                    ? `height-lg-all max-width`
                    : `height-md-all max-width`
                }
              >
                <title>{appleStoreAltText}</title>
                <path fill="#A6A6A6" d="M132.532 0H11.474c-.442 0-.878 0-1.318.003-.368.002-.734.009-1.105.015-.809.01-1.615.08-2.412.212a8.036 8.036 0 0 0-2.287.752 7.746 7.746 0 0 0-3.366 3.36 7.906 7.906 0 0 0-.752 2.284 15.549 15.549 0 0 0-.216 2.402c-.01.368-.012.737-.018 1.105V37.87c.006.373.007.734.018 1.107.01.805.082 1.608.216 2.402a7.89 7.89 0 0 0 .752 2.285 7.45 7.45 0 0 0 1.418 1.937 7.548 7.548 0 0 0 1.948 1.415c.72.365 1.49.62 2.287.757.797.13 1.604.201 2.412.212.371.008.737.013 1.105.013.44.002.876.002 1.318.002h121.058c.433 0 .872 0 1.305-.002.366 0 .742-.005 1.109-.013.807-.01 1.611-.081 2.407-.212a8.204 8.204 0 0 0 2.296-.757 7.546 7.546 0 0 0 1.946-1.415 7.67 7.67 0 0 0 1.422-1.937c.363-.72.615-1.49.745-2.285a16.12 16.12 0 0 0 .223-2.402c.005-.373.005-.734.005-1.107.01-.436.01-.87.01-1.312V11.444c0-.44 0-.876-.01-1.31 0-.369 0-.738-.005-1.106a16.124 16.124 0 0 0-.223-2.402 7.933 7.933 0 0 0-.745-2.284 7.773 7.773 0 0 0-3.368-3.36 8.159 8.159 0 0 0-2.296-.752c-.796-.133-1.6-.203-2.407-.212-.367-.006-.743-.013-1.109-.016C133.404 0 132.965 0 132.532 0Z" />
                <path fill="#000" d="M10.162 46.95c-.366 0-.724-.005-1.088-.013a15.312 15.312 0 0 1-2.25-.196 7.095 7.095 0 0 1-1.993-.657 6.503 6.503 0 0 1-1.681-1.22 6.387 6.387 0 0 1-1.228-1.676 6.854 6.854 0 0 1-.653-1.989 14.859 14.859 0 0 1-.2-2.25c-.008-.253-.018-1.095-.018-1.095v-27.72s.01-.83.017-1.074c.01-.753.076-1.504.2-2.247A6.893 6.893 0 0 1 1.92 4.82a6.697 6.697 0 0 1 2.91-2.905 7.021 7.021 0 0 1 1.989-.652c.746-.122 1.5-.188 2.257-.197l1.086-.015H133.83l1.099.015a14.95 14.95 0 0 1 2.236.195c.699.119 1.377.34 2.011.658a6.72 6.72 0 0 1 2.906 2.904c.313.623.53 1.29.644 1.978.125.75.195 1.506.209 2.265.003.34.003.705.003 1.068.01.45.01.879.01 1.31v25.115c0 .436 0 .861-.01 1.29 0 .39 0 .748-.004 1.116a15.215 15.215 0 0 1-.206 2.224 6.846 6.846 0 0 1-.65 2.004 6.56 6.56 0 0 1-1.222 1.663c-.491.496-1.06.91-1.684 1.227a7.072 7.072 0 0 1-2.007.66c-.744.12-1.496.186-2.249.195-.353.008-.722.013-1.08.013l-1.305.002-122.37-.002Z" />
                <path fill="#fff" d="M29.806 24.36a5.925 5.925 0 0 1 2.836-4.982 6.09 6.09 0 0 0-4.803-2.589c-2.02-.211-3.98 1.206-5.01 1.206-1.05 0-2.635-1.185-4.342-1.15a6.41 6.41 0 0 0-3.124.93 6.385 6.385 0 0 0-2.258 2.344c-2.328 4.018-.592 9.923 1.638 13.17 1.115 1.591 2.419 3.368 4.124 3.305 1.67-.07 2.293-1.062 4.308-1.062 1.996 0 2.58 1.062 4.32 1.022 1.792-.03 2.92-1.598 3.997-3.203a13.142 13.142 0 0 0 1.827-3.711 5.751 5.751 0 0 1-2.553-2.113 5.727 5.727 0 0 1-.96-3.166Zm-3.287-9.707a5.836 5.836 0 0 0 1.341-4.189A5.972 5.972 0 0 0 24 12.456a5.56 5.56 0 0 0-1.376 4.034 4.945 4.945 0 0 0 3.895-1.837Zm24.386 17.915H45.21l-1.368 4.027H41.43l5.395-14.901h2.506l5.395 14.901h-2.453l-1.367-4.027Zm-5.106-1.86h4.515l-2.226-6.536h-.062l-2.227 6.537Zm20.578.456c0 3.376-1.812 5.545-4.546 5.545a3.702 3.702 0 0 1-3.428-1.9h-.052v5.38h-2.236V25.731h2.164v1.807h.041a3.859 3.859 0 0 1 3.47-1.92c2.765 0 4.587 2.179 4.587 5.546Zm-2.298 0c0-2.2-1.14-3.646-2.88-3.646-1.708 0-2.857 1.476-2.857 3.646 0 2.189 1.149 3.655 2.858 3.655 1.739 0 2.879-1.436 2.879-3.655Zm14.29 0c0 3.376-1.812 5.545-4.547 5.545a3.702 3.702 0 0 1-3.428-1.9h-.052v5.38h-2.236V25.731h2.165v1.807h.04a3.86 3.86 0 0 1 3.47-1.92c2.765 0 4.588 2.179 4.588 5.546Zm-2.299 0c0-2.2-1.14-3.646-2.88-3.646-1.708 0-2.857 1.476-2.857 3.646 0 2.189 1.15 3.655 2.858 3.655 1.74 0 2.88-1.436 2.88-3.655Zm10.224 1.279c.166 1.478 1.605 2.448 3.572 2.448 1.885 0 3.242-.97 3.242-2.302 0-1.157-.818-1.85-2.755-2.324l-1.937-.466c-2.744-.66-4.018-1.94-4.018-4.017 0-2.57 2.247-4.337 5.438-4.337 3.157 0 5.322 1.766 5.395 4.337h-2.257c-.136-1.487-1.368-2.384-3.17-2.384-1.802 0-3.034.908-3.034 2.23 0 1.053.787 1.673 2.713 2.148l1.647.403c3.066.723 4.34 1.95 4.34 4.13 0 2.788-2.227 4.535-5.77 4.535-3.313 0-5.55-1.706-5.695-4.4h2.289Zm14.002-9.283v2.57h2.072v1.767h-2.072v5.99c0 .93.415 1.363 1.325 1.363.246-.004.492-.021.736-.051v1.755a6.158 6.158 0 0 1-1.242.103c-2.206 0-3.066-.826-3.066-2.933v-6.227h-1.584V25.73h1.584v-2.57h2.247Zm3.272 8.004c0-3.419 2.019-5.567 5.167-5.567 3.159 0 5.168 2.148 5.168 5.567 0 3.427-1.999 5.566-5.168 5.566-3.168 0-5.167-2.139-5.167-5.566Zm8.057 0c0-2.345-1.078-3.73-2.89-3.73s-2.889 1.395-2.889 3.73c0 2.354 1.077 3.727 2.889 3.727 1.812 0 2.89-1.373 2.89-3.727Zm4.122-5.434h2.133v1.85h.052a2.589 2.589 0 0 1 .96-1.445 2.606 2.606 0 0 1 1.66-.518c.258 0 .515.027.766.083v2.086a3.13 3.13 0 0 0-1.004-.135 2.26 2.26 0 0 0-1.742.716 2.23 2.23 0 0 0-.49.828 2.232 2.232 0 0 0-.099.956v6.444h-2.236V25.731Zm15.882 7.674c-.301 1.972-2.227 3.326-4.691 3.326-3.169 0-5.137-2.118-5.137-5.515 0-3.408 1.978-5.618 5.043-5.618 3.014 0 4.91 2.065 4.91 5.36v.763h-7.695v.135a2.823 2.823 0 0 0 1.755 2.87 2.85 2.85 0 0 0 1.176.207 2.47 2.47 0 0 0 2.516-1.528h2.123Zm-7.56-3.242h5.447a2.606 2.606 0 0 0-1.618-2.562 2.629 2.629 0 0 0-1.054-.196 2.764 2.764 0 0 0-2.565 1.7c-.139.335-.21.694-.21 1.058Zm-78.55-19.685a3.183 3.183 0 0 1 2.533.982 3.166 3.166 0 0 1 .845 2.576c0 2.288-1.24 3.602-3.378 3.602h-2.594v-7.16h2.594Zm-1.479 6.148h1.354a2.263 2.263 0 0 0 1.799-.726 2.247 2.247 0 0 0 .569-1.85 2.251 2.251 0 0 0-1.392-2.398 2.27 2.27 0 0 0-.976-.162H44.04v5.136Zm6.117-1.692a2.553 2.553 0 0 1 1.517-2.585 2.574 2.574 0 0 1 2.938.62 2.56 2.56 0 0 1 .656 1.965 2.554 2.554 0 0 1-1.516 2.59 2.573 2.573 0 0 1-3.477-1.537 2.554 2.554 0 0 1-.118-1.053Zm4.01 0c0-1.171-.527-1.856-1.453-1.856-.93 0-1.452.685-1.452 1.856 0 1.18.523 1.86 1.452 1.86.926 0 1.454-.684 1.454-1.86Zm7.894 2.704h-1.109l-1.12-3.98h-.085l-1.115 3.98h-1.099l-1.493-5.403h1.084l.971 4.123h.08l1.114-4.123h1.026l1.114 4.123h.085l.966-4.123h1.07l-1.489 5.403Zm2.744-5.403h1.03v.858h.08a1.619 1.619 0 0 1 1.617-.963 1.768 1.768 0 0 1 1.422.556 1.755 1.755 0 0 1 .453 1.454v3.498h-1.069v-3.23c0-.868-.378-1.3-1.17-1.3a1.248 1.248 0 0 0-.962.388 1.24 1.24 0 0 0-.331.981v3.161h-1.07v-5.403Zm6.306-2.11h1.07v7.513h-1.07v-7.513Zm2.556 4.809a2.555 2.555 0 0 1 1.517-2.585 2.574 2.574 0 0 1 2.939.62 2.558 2.558 0 0 1 .656 1.965 2.552 2.552 0 0 1-.654 1.969 2.567 2.567 0 0 1-1.902.84 2.575 2.575 0 0 1-1.901-.84 2.558 2.558 0 0 1-.655-1.969Zm4.011 0c0-1.171-.528-1.856-1.453-1.856-.93 0-1.453.685-1.453 1.856 0 1.18.523 1.86 1.453 1.86.925 0 1.453-.684 1.453-1.86Zm2.226 1.177c0-.973.727-1.534 2.016-1.614l1.468-.084v-.467c0-.57-.379-.893-1.11-.893-.597 0-1.01.219-1.13.6h-1.034c.109-.927.984-1.522 2.214-1.522 1.358 0 2.124.674 2.124 1.815v3.692h-1.03v-.76h-.084a1.821 1.821 0 0 1-1.627.85 1.64 1.64 0 0 1-1.663-.956 1.628 1.628 0 0 1-.144-.662Zm3.484-.463v-.451l-1.324.084c-.746.05-1.084.303-1.084.78 0 .486.423.769 1.004.769a1.28 1.28 0 0 0 1.404-1.182Zm2.47-.714c0-1.707.88-2.789 2.249-2.789a1.789 1.789 0 0 1 1.662.948h.08v-2.968h1.069v7.513h-1.025v-.854h-.084a1.878 1.878 0 0 1-1.702.943c-1.378 0-2.25-1.081-2.25-2.793Zm1.105 0c0 1.146.541 1.836 1.447 1.836.902 0 1.459-.7 1.459-1.831 0-1.126-.563-1.836-1.459-1.836-.9 0-1.447.694-1.447 1.831Zm8.38 0a2.553 2.553 0 0 1 1.516-2.585 2.574 2.574 0 0 1 2.939.62 2.564 2.564 0 0 1 .655 1.965 2.542 2.542 0 0 1-.654 1.969 2.567 2.567 0 0 1-1.901.84 2.573 2.573 0 0 1-2.437-1.756 2.552 2.552 0 0 1-.118-1.053Zm4.01 0c0-1.171-.527-1.856-1.453-1.856-.93 0-1.453.685-1.453 1.856 0 1.18.523 1.86 1.453 1.86.926 0 1.453-.684 1.453-1.86Zm2.535-2.699h1.03v.858h.08a1.624 1.624 0 0 1 1.617-.963 1.766 1.766 0 0 1 1.815 1.231c.078.252.099.518.06.78v3.497h-1.069v-3.23c0-.868-.379-1.3-1.17-1.3a1.244 1.244 0 0 0-1.293 1.37v3.16h-1.07v-5.403Zm10.645-1.346v1.37h1.174v.899h-1.174v2.778c0 .566.234.814.766.814.136 0 .272-.009.408-.025v.889a3.517 3.517 0 0 1-.582.054c-1.189 0-1.663-.417-1.663-1.459v-3.051h-.86v-.899h.86v-1.37h1.071Zm2.634-.764h1.06v2.978h.085a1.662 1.662 0 0 1 1.652-.968 1.788 1.788 0 0 1 1.799 1.241c.08.25.102.514.067.774v3.488h-1.07v-3.225c0-.863-.403-1.3-1.159-1.3a1.264 1.264 0 0 0-1.364 1.37v3.155h-1.069l-.001-7.513Zm10.899 6.054a2.194 2.194 0 0 1-2.348 1.563 2.47 2.47 0 0 1-1.899-.816 2.45 2.45 0 0 1-.604-1.973 2.48 2.48 0 0 1 .603-1.98 2.5 2.5 0 0 1 1.895-.842c1.508 0 2.418 1.027 2.418 2.723v.373h-3.827v.06a1.423 1.423 0 0 0 .378 1.092 1.43 1.43 0 0 0 1.065.455 1.304 1.304 0 0 0 1.289-.655h1.03Zm-3.762-1.741h2.737a1.297 1.297 0 0 0-.802-1.3 1.307 1.307 0 0 0-.532-.1 1.385 1.385 0 0 0-1.403 1.4Z" />
              </svg>
            ) : appsDownload === "google-lg" || appsDownload === "google-sm" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 167 48" 
                className={
                  appsDownload == "google-lg"
                    ? `height-lg-all max-width`
                    : `height-md-all max-width`
                }
              >
                <title>{googleStoreAltText}</title>
                <path fill="#100F0D" d="M159.91 48H6.15C2.768 48 0 45.3 0 42V6c0-3.3 2.768-6 6.15-6h153.76c3.382 0 6.15 2.7 6.15 6v36c0 3.3-2.768 6-6.15 6Z" />
                        <path fill="#A2A2A1" d="M159.91 0H6.15C2.768 0 0 2.7 0 6v36c0 3.3 2.768 6 6.15 6h153.76c3.382 0 6.15-2.7 6.15-6V6c0-3.3-2.768-6-6.15-6Zm0 .96c2.848 0 5.166 2.26 5.166 5.04v36c0 2.78-2.318 5.04-5.166 5.04H6.15C3.302 47.04.985 44.78.985 42V6C.985 3.22 3.302.96 6.15.96h153.76Z" />
                        <path fill="#fff" d="M131.54 36h2.295V20.998h-2.295V36Zm20.674-9.598-2.631 6.504h-.079l-2.73-6.504h-2.473l4.096 9.09-2.336 5.057h2.394l6.312-14.146h-2.553Zm-13.017 7.894c-.751 0-1.8-.367-1.8-1.274 0-1.158 1.306-1.602 2.433-1.602 1.009 0 1.485.212 2.097.502-.178 1.39-1.404 2.374-2.73 2.374Zm.278-8.222c-1.662 0-3.383.714-4.095 2.297l2.037.83c.435-.83 1.246-1.1 2.097-1.1 1.187 0 2.394.694 2.413 1.93v.154c-.415-.232-1.306-.579-2.393-.579-2.197 0-4.432 1.177-4.432 3.377 0 2.008 1.8 3.3 3.818 3.3 1.542 0 2.395-.675 2.927-1.466h.08v1.158h2.217v-5.752c0-2.663-2.04-4.149-4.669-4.149Zm-14.186 2.154h-3.265v-5.142h3.265a2.631 2.631 0 0 1 2.69 2.571 2.645 2.645 0 0 1-2.69 2.571Zm-.059-7.23h-5.5V36h2.294v-5.683h3.206c2.544 0 5.045-1.797 5.045-4.66 0-2.861-2.501-4.66-5.045-4.66Zm-29.99 13.3c-1.587 0-2.914-1.295-2.914-3.073 0-1.799 1.328-3.113 2.913-3.113 1.566 0 2.794 1.314 2.794 3.113 0 1.778-1.228 3.073-2.794 3.073Zm2.635-7.056h-.08c-.515-.6-1.506-1.14-2.754-1.14-2.616 0-5.014 2.242-5.014 5.123 0 2.86 2.398 5.084 5.014 5.084 1.248 0 2.24-.542 2.754-1.16h.08v.734c0 1.954-1.07 2.997-2.794 2.997-1.407 0-2.279-.986-2.636-1.817l-2.001.812c.574 1.353 2.1 3.016 4.637 3.016 2.695 0 4.974-1.547 4.974-5.317V26.41h-2.18v.83ZM101.64 36h2.298V20.997h-2.298V36Zm5.687-4.95a2.83 2.83 0 0 1 2.735-2.977c.912 0 1.684.445 1.943 1.083l-4.678 1.895Zm7.134-1.701c-.436-1.14-1.764-3.247-4.479-3.247-2.695 0-4.934 2.068-4.934 5.103 0 2.861 2.22 5.104 5.192 5.104 2.397 0 3.785-1.43 4.36-2.262l-1.784-1.16c-.594.85-1.406 1.411-2.576 1.411-1.169 0-2.002-.522-2.536-1.546l6.995-2.823-.238-.58Zm-55.733-1.681v2.165h5.311c-.159 1.218-.575 2.107-1.21 2.726-.772.753-1.981 1.585-4.101 1.585-3.27 0-5.826-2.571-5.826-5.761s2.556-5.762 5.826-5.762c1.764 0 3.052.677 4.003 1.547l1.566-1.527c-1.328-1.237-3.092-2.185-5.57-2.185-4.479 0-8.244 3.558-8.244 7.927s3.765 7.926 8.245 7.926c2.417 0 4.24-.773 5.668-2.223 1.466-1.43 1.922-3.441 1.922-5.066 0-.502-.04-.966-.12-1.352h-7.47Zm13.629 6.63c-1.586 0-2.954-1.276-2.954-3.093 0-1.836 1.368-3.093 2.954-3.093 1.585 0 2.952 1.257 2.952 3.093 0 1.818-1.367 3.093-2.953 3.093Zm0-8.197c-2.895 0-5.252 2.146-5.252 5.104 0 2.939 2.357 5.104 5.252 5.104 2.893 0 5.251-2.165 5.251-5.104 0-2.958-2.358-5.103-5.251-5.103Zm11.455 8.197c-1.584 0-2.952-1.276-2.952-3.093 0-1.836 1.368-3.093 2.952-3.093 1.586 0 2.953 1.257 2.953 3.093 0 1.818-1.367 3.093-2.953 3.093Zm0-8.197c-2.893 0-5.25 2.146-5.25 5.104 0 2.939 2.357 5.104 5.25 5.104 2.894 0 5.252-2.165 5.252-5.104 0-2.958-2.358-5.103-5.252-5.103Z" />
                <path fill="#EB3131" d="m25.482 23.309-13.095 13.56.002.007c.401 1.473 1.78 2.557 3.416 2.557a3.59 3.59 0 0 0 1.795-.475l.042-.024 14.74-8.298-6.9-7.327Z" />
                <path fill="#F6B60B" d="m38.73 21-.012-.009-6.364-3.599-7.17 6.224 7.195 7.019 6.33-3.564c1.11-.584 1.863-1.726 1.863-3.042 0-1.307-.743-2.443-1.842-3.03Z" />
                <path fill="#5778C5" d="M12.382 11.132c-.08.284-.12.58-.12.888v23.961c0 .308.04.606.12.888L25.93 23.656 12.382 11.132Z" />
                <path fill="#3BAD49" d="m25.58 24 6.778-6.61-14.724-8.328a3.613 3.613 0 0 0-1.829-.494c-1.636 0-3.016 1.087-3.418 2.56v.004L25.58 24.001Z" />
                <path fill="#fff" stroke="#fff" stroke-miterlimit="10" stroke-width=".267" d="M58.297 11.737H54.71v.867h2.689c-.073.706-.361 1.261-.848 1.665-.486.403-1.107.605-1.84.605-.806 0-1.489-.274-2.048-.817-.548-.555-.826-1.242-.826-2.068 0-.827.278-1.514.826-2.068.56-.544 1.242-.816 2.047-.816.413 0 .807.07 1.169.221.361.152.651.364.878.636l.682-.666A3.073 3.073 0 0 0 56.25 8.5a4.146 4.146 0 0 0-1.541-.283c-1.074 0-1.985.364-2.73 1.09-.743.727-1.116 1.623-1.116 2.683 0 1.059.373 1.957 1.117 2.683.744.725 1.655 1.09 2.729 1.09 1.128 0 2.028-.353 2.72-1.07.609-.596.92-1.402.92-2.41 0-.171-.02-.354-.051-.545Zm1.39-3.36V15.6h4.323v-.887h-3.37v-2.291h3.04v-.867h-3.04V9.266h3.371v-.889h-4.322Zm10.384.89v-.89h-5.087v.89h2.068V15.6h.951V9.266h2.068Zm4.606-.89h-.951V15.6h.951V8.377Zm6.28.89v-.89H75.87v.89h2.068V15.6h.95V9.266h2.07Zm9.6.05c-.734-.737-1.633-1.1-2.708-1.1-1.076 0-1.975.363-2.71 1.088-.734.717-1.095 1.615-1.095 2.684 0 1.068.361 1.966 1.095 2.683.735.725 1.634 1.09 2.71 1.09 1.065 0 1.974-.365 2.709-1.09.734-.716 1.095-1.615 1.095-2.684 0-1.059-.361-1.955-1.095-2.671Zm-4.735.603c.547-.543 1.22-.815 2.027-.815.806 0 1.478.272 2.017.815.547.535.816 1.232.816 2.069 0 .838-.269 1.533-.816 2.068-.538.543-1.211.817-2.017.817-.807 0-1.48-.274-2.027-.817-.537-.546-.806-1.23-.806-2.069 0-.836.27-1.522.806-2.068Zm8.091 1.182-.04-1.394h.04l3.764 5.892h.993V8.377h-.95v4.226l.04 1.394h-.04l-3.599-5.62h-1.159V15.6h.951v-4.498Z" />
              </svg>
            ) : (
              <></>
            )}
          </a>
        </>
      ) : (
        <>
        {namedLinkedAnchor && namedLinkedAnchor!= '' && <a id={`${namedLinkedAnchor}`}></a>}
          <a
            id= {linkId}
            className={[
              `${propsClassNames} ${marginClasses}`,
              overlay ? "cta-overlay" : "",
              variant === LinksVariant.withIcon
                ? onDark
                  ? `color-white font-bold link-icon hover`
                  : `color-cobalt font-bold link-icon hover`
                : variant,
            ].join(" ")}
            onKeyDown={handleKeyEvent}
            onClick={handleOnClick}
            target={isNewTab && "_blank"}
            aria-label={accessibilityText}
            {...{ href: href }}
            {...{ tabIndex: href ? undefined : 0 }}
            {...{
              role: !props.role ? (!href ? "button" : undefined) : undefined,
            }}
            ref={anchorRef}
            {...props}
          >
            {React.Children.map(children, (child, idx) => {
              if (React.isValidElement(child) && child.type === Links.IconSvg) {
                return React.cloneElement(child as React.ReactElement<any>, {
                  idx,
                });
              }
              if (hasIcon) {
                return <span>{child}</span>;
              } else {
                return child;
              }
            })}
          </a>
        </>
      )}
      {isModal && showModal && (
        <Modal
          size={ModalSize.LARGE}
          responsiveFullScreen={false}
          onClose={() => {
            closeModal();
          }}
          inScrim={false}
        >
          <Modal.Body>{modalBody}</Modal.Body>
        </Modal>
      )}
      {showVideo && (
        <>
          <Modal
            size={ModalSize.FULLSCREEN}
            responsiveFullScreen={false}
            close={false}
            onClose={() => {
              setShowVideo(videoData);
            }}
            inScrim={false}
          >
            <Modal.Body>
              <BackgroundVideo
                {...showVideo.toProps()}
                {...(showVideo.toProps().playerView && {
                  parentCallback: handleCallbackForVideo,
                })}
              />
              <button
                id="closeVideo"
                title="Close Video"
                arial-label="Close Video"
                className="close-btn absolute z2 width-md-all height-md-all bg-white type-14 top10 radius-lg"
                onClick={() => setShowVideo(videoData)}
                data-testid="CloseBtn"
                style={{ right: "50px" }}
              >
                <IconSvg
                  size={IconSvgSizes.MD}
                  svgNodeStr={`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path d="M17.41 16l4.25 4.24-1.42 1.42L16 17.41l-4.24 4.25-1.42-1.42L14.59 16l-4.25-4.24 1.42-1.42L16 14.59l4.24-4.25 1.42 1.42z"/></svg>`}
                />
              </button>
            </Modal.Body>
          </Modal>
        </>
      )}
    </>
  );
}
